/**
 * For You API (Backend/foryou_routes.py). Every call sends ?user_id= and the
 * bearer token; a 401 renews the session once (apiService's single-flight
 * refresh) and asks again. Errors come back as the server's sentence.
 */
import { apiService } from '../../services/apiService';
import { featureNotEnabled, FEATURE_NOT_ENABLED_TEXT } from '../../utils/featureAccess';

export interface TechMini {
  id: string;
  title: string;
  /** Not sent to an Explorer. */
  university?: string;
  country?: string;
  /** First sub-category (else the major category): the "· Department" part. */
  area: string;
  tags: string[];
  available: boolean;
}
export interface Rec extends TechMini { relevance: number; match: 'strong' | 'good'; saved: boolean }
export interface Problem {
  id: number;
  position: number;
  text: string;
  /** done | empty | pending (queued, running or waiting for a retry) */
  status: 'done' | 'empty' | 'pending';
  job_status: string;
  matched_at: string | null;
  match_count: number;
  retrying: boolean;
  recs: Rec[];
}
export interface SavedTech extends TechMini { asset_id: string; saved_at: string | null }
export interface KeywordRow {
  keyword: string;
  keyword_norm: string;
  added_at: string | null;
  /** ok | pending (pool not built yet) | empty (nothing at the threshold) */
  state: 'ok' | 'pending' | 'empty';
  tech_id: string | null;
  tech?: TechMini;
  week_key: string;
  week_label: string;
}
export interface PageSettings {
  max_problems: number; max_chars: number; max_keywords: number; strong_match: number;
  top_shown: number; full_list_max: number; share_max_recipients: number; share_max_techs: number;
  share_note_max: number;
  sections: { problems: boolean; recommended: boolean; saved: boolean; interests: boolean };
}
export interface ForYouState {
  settings: PageSettings;
  week: { key: string; label: string };
  problems: Problem[];
  saved: SavedTech[];
  keywords: KeywordRow[];
}

export interface Res<T> { ok: boolean; data: T | null; status: number; error: string }

const uid = (): string => { try { return apiService.getStoredUserId() || ''; } catch { return ''; } };
const token = (): string => { try { return apiService.getStoredAccessToken() || ''; } catch { return ''; } };

const refresh = async (): Promise<boolean> => {
  try {
    const fn = (apiService as any).refreshAccessToken;
    return typeof fn === 'function' ? !!(await fn.call(apiService)) : false;
  } catch { return false; }
};

async function once(method: string, path: string, body?: unknown): Promise<{ status: number; json: any }> {
  const sep = path.includes('?') ? '&' : '?';
  const url = `${apiService.getApiBaseUrl()}${path}${sep}user_id=${encodeURIComponent(uid())}`;
  const t = token();
  const res = await fetch(url, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    mode: 'cors',
    credentials: 'omit',
  });
  return { status: res.status, json: await res.json().catch(() => ({})) };
}

const errText = (j: any, status: number): string => {
  const d = j?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d)) return 'Please check what you entered.';
  if (status === 401) return 'Please sign in again.';
  if (status === 429) return 'Too many requests — please wait a moment and try again.';
  return `Something went wrong (${status || 'network'}). Please try again.`;
};

async function call<T>(method: string, path: string, body?: unknown): Promise<Res<T>> {
  if (!uid()) return { ok: false, data: null, status: 401, error: 'Please sign in to see For You.' };
  try {
    let r = await once(method, path, body);
    if (r.status === 401 && await refresh()) r = await once(method, path, body);
    if (r.status >= 200 && r.status < 300) return { ok: true, data: r.json as T, status: r.status, error: '' };
    // Feature access changed under this tab: App re-asks and swaps to the legacy page.
    if (featureNotEnabled(r.status, r.json)) return { ok: false, data: null, status: r.status, error: FEATURE_NOT_ENABLED_TEXT };
    return { ok: false, data: null, status: r.status, error: errText(r.json, r.status) };
  } catch {
    return { ok: false, data: null, status: 0, error: 'Network error — check your connection and try again.' };
  }
}

const B = '/api/v1/foryou';
export const fy = {
  state: () => call<ForYouState>('GET', `${B}/state`),
  addProblem: (text: string) => call<ForYouState & { added: number }>('POST', `${B}/problems`, { text }),
  editProblem: (id: number, text: string) => call<ForYouState>('PUT', `${B}/problems/${id}`, { text }),
  removeProblem: (id: number) => call<ForYouState>('DELETE', `${B}/problems/${id}`),
  matches: (id: number) => call<{ problem_id: number; status: string; items: Rec[] }>('GET', `${B}/problems/${id}/matches`),
  addKeyword: (keyword: string) => call<ForYouState>('POST', `${B}/keywords`, { keyword }),
  removeKeyword: (keyword: string) => call<ForYouState>('DELETE', `${B}/keywords?keyword=${encodeURIComponent(keyword)}`),
  saved: () => call<{ items: SavedTech[] }>('GET', `${B}/saved`),
  share: (recipients: string, note: string, tech_ids: string[]) =>
    call<{ ok: boolean; sent: number; recipients: number; message: string }>('POST', `${B}/share`, { recipients, note, tech_ids }),
  copy: (note: string, tech_ids: string[]) => call<{ text: string; count: number }>('POST', `${B}/share/copy`, { note, tech_ids }),
};

/** "2 days ago", "just now", "3 weeks ago". */
export const ago = (iso: string | null): string => {
  if (!iso) return 'a while ago';
  // A time with no zone is UTC (the server's clock): read as local time it
  // put a save made seconds ago 5–6 hours back for anyone in India.
  const zoned = /([zZ]|[+-]\d\d:?\d\d)$/.test(iso) || !/T\d/.test(iso) ? iso : `${iso}Z`;
  const t = new Date(zoned).getTime();
  if (isNaN(t)) return 'a while ago';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 90) return 'just now';
  const m = s / 60, h = m / 60, d = h / 24, w = d / 7, mo = d / 30, y = d / 365;
  const f = (n: number, u: string) => `${Math.floor(n)} ${u}${Math.floor(n) === 1 ? '' : 's'} ago`;
  if (m < 60) return f(m, 'minute');
  if (h < 24) return f(h, 'hour');
  if (d < 7) return f(d, 'day');
  if (w < 5) return f(w, 'week');
  if (mo < 12) return f(mo, 'month');
  return f(y, 'year');
};

export const plural = (n: number, one: string, many?: string) => `${n} ${n === 1 ? one : (many || one + 's')}`;
